import { z } from 'zod';
import { isoTimestampSchema, uuidSchema } from './common';
import { stockMovementSchema } from './inventory';

/*
 * Procurement (spec §5.8, ADR-0020). Document quantities are in the line's unit at scale
 * 1000; prices are net of tax per one unit, integer minor units in the branch currency.
 */

const dateSchema = z.iso.date();
const positiveScaled = z
  .number()
  .int()
  .refine((n) => Number.isSafeInteger(n) && n > 0, 'Must be a positive safe integer');
const nonNegativeScaled = z
  .number()
  .int()
  .refine((n) => Number.isSafeInteger(n) && n >= 0, 'Must be a non-negative safe integer');
const priceSchema = z.number().int().min(0).refine(Number.isSafeInteger);
const optionalText = (max: number) => z.string().trim().min(1).max(max).nullable();

// ---- Suppliers (tenant master data)

export const supplierSchema = z.object({
  id: uuidSchema,
  code: z.string(),
  name: z.string(),
  contact_name: z.string().nullable(),
  phone: z.string().nullable(),
  email: z.string().nullable(),
  tax_id: z.string().nullable(),
  address: z.string().nullable(),
  payment_terms_days: z.number().int(),
  active: z.boolean(),
  version: z.number().int(),
});
export type Supplier = z.infer<typeof supplierSchema>;
export const supplierListResponseSchema = z.object({ items: z.array(supplierSchema) });

const supplierFields = {
  name: z.string().trim().min(1).max(120),
  contact_name: optionalText(80),
  phone: optionalText(40),
  email: z.email().max(254).nullable(),
  tax_id: optionalText(40),
  address: optionalText(300),
  payment_terms_days: z.number().int().min(0).max(365),
};
export const createSupplierRequestSchema = z.object({
  code: z
    .string()
    .trim()
    .min(1)
    .max(20)
    .regex(/^[A-Za-z0-9._-]+$/, 'Letters, digits, dot, dash and underscore only'),
  ...supplierFields,
  contact_name: supplierFields.contact_name.default(null),
  phone: supplierFields.phone.default(null),
  email: supplierFields.email.default(null),
  tax_id: supplierFields.tax_id.default(null),
  address: supplierFields.address.default(null),
  payment_terms_days: supplierFields.payment_terms_days.default(0),
});
export const updateSupplierRequestSchema = z.object({
  expected_version: z.number().int().nonnegative(),
  ...z.object(supplierFields).partial().shape,
  active: z.boolean().optional(),
});

// ---- Purchase orders (branch)

export const PURCHASE_ORDER_STATUSES = [
  'draft',
  'pending_approval',
  'approved',
  'partially_received',
  'received',
  'closed',
  'cancelled',
] as const;
export const purchaseOrderStatusSchema = z.enum(PURCHASE_ORDER_STATUSES);
export type PurchaseOrderStatus = z.infer<typeof purchaseOrderStatusSchema>;

export const purchaseOrderLineSchema = z.object({
  id: uuidSchema,
  line_no: z.number().int(),
  item_id: uuidSchema,
  sku: z.string(),
  item_name: z.string(),
  base_unit: z.enum(['g', 'ml', 'pcs']),
  unit: z.string(),
  unit_factor_scaled: z.number().int(),
  qty_ordered_scaled: z.number().int(),
  qty_received_scaled: z.number().int(),
  unit_price_minor: z.number().int(),
  line_total_minor: z.number().int(),
});
export type PurchaseOrderLine = z.infer<typeof purchaseOrderLineSchema>;

export const purchaseOrderSchema = z.object({
  id: uuidSchema,
  po_number: z.string(),
  status: purchaseOrderStatusSchema,
  supplier_id: uuidSchema,
  supplier_name: z.string(),
  currency: z.string(),
  expected_date: dateSchema.nullable(),
  notes: z.string().nullable(),
  total_minor: z.number().int(),
  created_by: uuidSchema,
  created_by_name: z.string().nullable(),
  approved_by: uuidSchema.nullable(),
  approved_by_name: z.string().nullable(),
  approved_at: isoTimestampSchema.nullable(),
  closed_reason: z.string().nullable(),
  closed_at: isoTimestampSchema.nullable(),
  created_at: isoTimestampSchema,
  purchase_request_id: uuidSchema.nullable(),
  /** Number of the source purchase request. */
  pr_number: z.string().nullable().optional(),
  budget_id: uuidSchema.nullable(),
  budget_label: z.string().nullable().optional(),
  approval_request_id: uuidSchema.nullable(),
  version: z.number().int(),
  lines: z.array(purchaseOrderLineSchema),
});
export type PurchaseOrder = z.infer<typeof purchaseOrderSchema>;

export const purchaseOrderSummarySchema = purchaseOrderSchema.omit({ lines: true }).extend({
  line_count: z.number().int(),
});
export type PurchaseOrderSummary = z.infer<typeof purchaseOrderSummarySchema>;
export const purchaseOrderListResponseSchema = z.object({
  items: z.array(purchaseOrderSummarySchema),
});
export const purchaseOrderListQuerySchema = z.object({
  status: purchaseOrderStatusSchema.optional(),
  supplier_id: uuidSchema.optional(),
  /** Orders that can still be received (approved or partially received). */
  receivable: z.enum(['true', 'false']).optional(),
});

export const purchaseOrderLineInputSchema = z.object({
  item_id: uuidSchema,
  /** Standard unit or the item's pack unit code. Default: base unit. */
  unit: z.string().trim().min(1).max(12).optional(),
  qty_ordered_scaled: positiveScaled,
  unit_price_minor: priceSchema,
});

export const createPurchaseOrderRequestSchema = z.object({
  supplier_id: uuidSchema,
  /** Budget line to commit against; null = no budget control. */
  budget_id: uuidSchema.nullable().default(null),
  expected_date: dateSchema.nullable().default(null),
  notes: optionalText(500).default(null),
  lines: z.array(purchaseOrderLineInputSchema).min(1).max(100),
});
/** Drafts only. `lines`, when given, replaces all lines. */
export const updatePurchaseOrderRequestSchema = z.object({
  expected_version: z.number().int().nonnegative(),
  supplier_id: uuidSchema.optional(),
  budget_id: uuidSchema.nullable().optional(),
  expected_date: dateSchema.nullable().optional(),
  notes: optionalText(500).optional(),
  lines: z.array(purchaseOrderLineInputSchema).min(1).max(100).optional(),
});
export const purchaseOrderActionRequestSchema = z.object({
  expected_version: z.number().int().nonnegative(),
});
export const purchaseOrderCloseRequestSchema = z.object({
  expected_version: z.number().int().nonnegative(),
  reason: z.string().trim().min(3).max(300),
});

// ---- Goods receipts (branch)

export const goodsReceiptLineSchema = z.object({
  id: uuidSchema,
  purchase_order_line_id: uuidSchema,
  item_id: uuidSchema,
  sku: z.string(),
  item_name: z.string(),
  base_unit: z.enum(['g', 'ml', 'pcs']),
  lot_tracked: z.boolean(),
  expiry_tracked: z.boolean(),
  unit: z.string(),
  unit_factor_scaled: z.number().int(),
  qty_ordered_scaled: z.number().int(),
  qty_received_scaled: z.number().int(),
  qty_rejected_scaled: z.number().int(),
  reject_reason: z.string().nullable(),
  unit_price_minor: z.number().int(),
  value_minor: z.number().int(),
  lot_code: z.string().nullable(),
  expiry_date: dateSchema.nullable(),
  manufactured_date: dateSchema.nullable(),
  hold: z.boolean(),
  lot_id: uuidSchema.nullable(),
});
export type GoodsReceiptLine = z.infer<typeof goodsReceiptLineSchema>;

export const goodsReceiptSchema = z.object({
  id: uuidSchema,
  gr_number: z.string(),
  status: z.enum(['draft', 'posted', 'cancelled']),
  purchase_order_id: uuidSchema,
  po_number: z.string(),
  supplier_id: uuidSchema,
  supplier_name: z.string(),
  location_id: uuidSchema,
  location_name: z.string(),
  delivery_note_ref: z.string().nullable(),
  notes: z.string().nullable(),
  currency: z.string(),
  total_minor: z.number().int(),
  received_at: isoTimestampSchema,
  business_date: dateSchema.nullable(),
  created_by_name: z.string().nullable(),
  posted_by_name: z.string().nullable(),
  posted_at: isoTimestampSchema.nullable(),
  version: z.number().int(),
  lines: z.array(goodsReceiptLineSchema),
});
export type GoodsReceipt = z.infer<typeof goodsReceiptSchema>;

export const goodsReceiptSummarySchema = goodsReceiptSchema.omit({ lines: true }).extend({
  line_count: z.number().int(),
});
export const goodsReceiptListResponseSchema = z.object({
  items: z.array(goodsReceiptSummarySchema),
});
export const goodsReceiptListQuerySchema = z.object({
  status: z.enum(['draft', 'posted', 'cancelled']).optional(),
  purchase_order_id: uuidSchema.optional(),
});

export const goodsReceiptLineInputSchema = z.object({
  purchase_order_line_id: uuidSchema,
  qty_received_scaled: nonNegativeScaled,
  qty_rejected_scaled: nonNegativeScaled.default(0),
  reject_reason: optionalText(200).default(null),
  lot_code: z.string().trim().min(1).max(40).nullable().default(null),
  expiry_date: dateSchema.nullable().default(null),
  manufactured_date: dateSchema.nullable().default(null),
  hold: z.boolean().default(false),
});

export const createGoodsReceiptRequestSchema = z.object({
  purchase_order_id: uuidSchema,
  /** Default: the branch's default stock location. */
  location_id: uuidSchema.optional(),
  delivery_note_ref: optionalText(60).default(null),
  notes: optionalText(500).default(null),
  lines: z.array(goodsReceiptLineInputSchema).min(1).max(100),
});
/** Drafts only. `lines`, when given, replaces all lines. */
export const updateGoodsReceiptRequestSchema = z.object({
  expected_version: z.number().int().nonnegative(),
  location_id: uuidSchema.optional(),
  delivery_note_ref: optionalText(60).optional(),
  notes: optionalText(500).optional(),
  lines: z.array(goodsReceiptLineInputSchema).min(1).max(100).optional(),
});
export const goodsReceiptActionRequestSchema = z.object({
  expected_version: z.number().int().nonnegative(),
});

export const goodsReceiptPostResultSchema = z.object({
  receipt: goodsReceiptSchema,
  movements: z.array(stockMovementSchema),
});
export type GoodsReceiptPostResult = z.infer<typeof goodsReceiptPostResultSchema>;

// ---- Purchase requests (P1 §6.4, ADR-0023)

export const PURCHASE_REQUEST_STATUSES = [
  'draft',
  'pending_approval',
  'approved',
  'rejected',
  'cancelled',
  'converted',
] as const;
export const purchaseRequestStatusSchema = z.enum(PURCHASE_REQUEST_STATUSES);

export const purchaseRequestLineSchema = z.object({
  id: uuidSchema,
  line_no: z.number().int(),
  item_id: uuidSchema,
  sku: z.string(),
  item_name: z.string(),
  base_unit: z.enum(['g', 'ml', 'pcs']),
  unit: z.string(),
  unit_factor_scaled: z.number().int(),
  qty_scaled: z.number().int(),
  estimated_price_minor: z.number().int(),
  line_total_minor: z.number().int(),
  suggested_supplier_id: uuidSchema.nullable(),
});

export const purchaseRequestSchema = z.object({
  id: uuidSchema,
  pr_number: z.string(),
  status: purchaseRequestStatusSchema,
  budget_id: uuidSchema.nullable(),
  budget_label: z.string().nullable(),
  currency: z.string(),
  needed_by: dateSchema.nullable(),
  notes: z.string().nullable(),
  total_minor: z.number().int(),
  requested_by: uuidSchema,
  requested_by_name: z.string().nullable(),
  approval_request_id: uuidSchema.nullable(),
  purchase_order_id: uuidSchema.nullable(),
  /** Number of the PO the request was converted into. */
  po_number: z.string().nullable().optional(),
  closed_reason: z.string().nullable(),
  created_at: isoTimestampSchema,
  version: z.number().int(),
  lines: z.array(purchaseRequestLineSchema),
});
export type PurchaseRequest = z.infer<typeof purchaseRequestSchema>;
export const purchaseRequestListResponseSchema = z.object({
  items: z.array(
    purchaseRequestSchema.omit({ lines: true }).extend({ line_count: z.number().int() }),
  ),
});
export const purchaseRequestListQuerySchema = z.object({
  status: purchaseRequestStatusSchema.optional(),
});

const prLineInput = z.object({
  item_id: uuidSchema,
  unit: z.string().trim().min(1).max(12).optional(),
  qty_scaled: positiveScaled,
  estimated_price_minor: priceSchema,
  suggested_supplier_id: uuidSchema.nullable().default(null),
});
export const createPurchaseRequestRequestSchema = z.object({
  budget_id: uuidSchema.nullable().default(null),
  needed_by: dateSchema.nullable().default(null),
  notes: optionalText(500).default(null),
  lines: z.array(prLineInput).min(1).max(100),
});
export const updatePurchaseRequestRequestSchema = z.object({
  expected_version: z.number().int().nonnegative(),
  budget_id: uuidSchema.nullable().optional(),
  needed_by: dateSchema.nullable().optional(),
  notes: optionalText(500).optional(),
  lines: z.array(prLineInput).min(1).max(100).optional(),
});
export const purchaseRequestActionRequestSchema = z.object({
  expected_version: z.number().int().nonnegative(),
});
export const purchaseRequestCancelRequestSchema = z.object({
  expected_version: z.number().int().nonnegative(),
  reason: z.string().trim().min(3).max(300),
});
/** Turns an approved request into a purchase order; prices may be negotiated per line. */
export const convertPurchaseRequestRequestSchema = z.object({
  expected_version: z.number().int().nonnegative(),
  supplier_id: uuidSchema,
  expected_date: dateSchema.nullable().default(null),
  prices: z
    .array(z.object({ line_id: uuidSchema, unit_price_minor: priceSchema }))
    .max(100)
    .default([]),
});
